'use client';
import React, { FC, ReactNode, useEffect, useRef, useState } from 'react';

import { Entry } from 'contentful';

import { useFlags } from 'launchdarkly-react-client-sdk';

import { NavMenu } from '@/components';

import {
  NavMenu as RedesignNavMenu,
  HamburgerMenu as RedesignHamburgerMenu,
} from '@/components/homepage-us/NavMenu';

import { TypeHeader } from '@/core/generated-types/TypeHeader';

import { useIsDesktop } from '@/hooks/useIsDesktop';

import { usePathname } from '@/navigation';

import { GlobalBanner } from '../GlobalBanner';
import { HamburgerMenu } from '../HamburgerMenu';
import { SimpleNav } from '../SimpleNav';

import { NAV_TYPES, NAV_URLS, isSubflowPathname } from './utils';

interface HeaderProps {
  header: TypeHeader;
}

export const Header: FC<HeaderProps> = ({ header }) => {
  const {
    enableGlobalBanner,
    globalBanner,
    linkSection1,
    linkSection2,
    userMenuLoggedOut,
    userMenuLoggedIn,
    logoutText,
    cartText,
    logo,
    mobileMenu,
    primaryCtaButton,
    playKitsByAgeLinks,
    playKitsDropdownLinks,
    playKistByAgeCta,
    shopAllPrimaryLinks,
    shopAllAdditionalLinks,
  } = header?.fields || {};

  const [clientSideStyles, setClientSideStyles] = useState({});

  const pathname = usePathname();

  const showSimpleNav = !useIsDesktop();

  const bannerRef = useRef<HTMLDivElement>(null);

  const [navType, setNavType] = useState<NAV_TYPES>(NAV_TYPES.NORMAL);

  //  US store has play kits by age links
  const isUSStore = process.env.NEXT_PUBLIC_REGION === 'US';

  const isAgeBasedPage = pathname?.includes('/the-play-kits/') ?? false;

  const isMainAgeBasedPage = pathname === '/products/the-play-kits';

  // mobile shop all navigation dropdown feature flag
  const {
    'sub-squad-enable-shop-all-navigation-dropdown': enableShopAllAsDropdown,
  } = useFlags();

  const isMobileShopAllAvailable =
    (shopAllPrimaryLinks || shopAllAdditionalLinks) && enableShopAllAsDropdown;

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const updateBannerHeight = () => {
        if (bannerRef.current) {
          setClientSideStyles({
            top: `-${bannerRef.current?.offsetHeight}px`,
          });
        } else {
          // If banner is not present, set top to 0 to maintain sticky position
          setClientSideStyles({
            top: '0',
          });
        }
      };

      updateBannerHeight();

      window.addEventListener('resize', updateBannerHeight);

      return () => {
        window.removeEventListener('resize', updateBannerHeight);
      };
    }
  }, [bannerRef.current?.offsetHeight]);

  const getNavComponent = (navType: NAV_TYPES): ReactNode => {
    const hasNav = linkSection1 && logo;

    if (!hasNav) return null;

    const commonProps = {
      linkSection1,
      linkSection2,
      userMenuLoggedOut,
      userMenuLoggedIn,
      logoutText,
      cartText,
      logo,
    };

    const specificProps: {
      playKitsByAgeLinks?: Entry<Record<string, any>>[];
      playKitsDropdownLinks?: Entry<Record<string, any>>[];
      playKistByAgeCta?: any;
      shopAllPrimaryLinks?: Entry<Record<string, any>>[];
      shopAllAdditionalLinks?: Entry<Record<string, any>>[];
    } =
      isUSStore && playKitsByAgeLinks && playKitsDropdownLinks
        ? { playKitsByAgeLinks, playKitsDropdownLinks }
        : {};

    // "Get started" CTA should be displayed on the main age-based and "/products/the-play-kits" pages
    if (isAgeBasedPage || isMainAgeBasedPage) {
      specificProps.playKistByAgeCta = playKistByAgeCta;
    }

    if (isMobileShopAllAvailable) {
      specificProps.shopAllPrimaryLinks = shopAllPrimaryLinks;
      specificProps.shopAllAdditionalLinks = shopAllAdditionalLinks;
    }

    switch (navType) {
      case NAV_TYPES.NORMAL: {
        const desktopNav = <NavMenu {...commonProps} {...specificProps} />;

        const mobileNav = mobileMenu ? (
          <HamburgerMenu
            menu={mobileMenu}
            {...commonProps}
            {...specificProps}
          />
        ) : null;

        return (
          <>
            {desktopNav}
            {mobileNav}
          </>
        );
      }

      case NAV_TYPES.SIMPLE: {
        return <SimpleNav logo={logo} />;
      }

      case NAV_TYPES.STICKY: {
        const desktopNav = (
          <RedesignNavMenu
            buttonCTA={primaryCtaButton}
            {...commonProps}
            {...specificProps}
          />
        );

        const mobileNav = mobileMenu ? (
          <RedesignHamburgerMenu
            menu={mobileMenu}
            buttonCTA={primaryCtaButton}
            {...commonProps}
            {...specificProps}
          />
        ) : null;

        return (
          <>
            {desktopNav}
            {mobileNav}
          </>
        );
      }

      default: {
        const desktopNav = <NavMenu {...commonProps} />;

        const mobileNav = mobileMenu ? (
          <HamburgerMenu menu={mobileMenu} {...commonProps} />
        ) : null;

        return (
          <>
            {desktopNav}
            {mobileNav}
          </>
        );
      }
    }
  };

  useEffect(() => {
    let newNavType = NAV_TYPES.NORMAL;

    const isStickyNavType = Boolean(pathname === NAV_URLS.HOME);

    const isSimpleNavType = Boolean(
      showSimpleNav && isSubflowPathname(pathname),
    );

    if (isStickyNavType) {
      newNavType = NAV_TYPES.STICKY;
    } else if (isSimpleNavType) {
      newNavType = NAV_TYPES.SIMPLE;
    }

    setNavType(newNavType);
  }, [pathname, showSimpleNav, isUSStore]);

  return (
    <div
      data-testid="le-header"
      className="sticky z-50"
      style={clientSideStyles}
    >
      {enableGlobalBanner && globalBanner && (
        <div ref={bannerRef}>
          <GlobalBanner {...globalBanner.fields} />
        </div>
      )}

      {getNavComponent(navType)}
    </div>
  );
};

export default Header;
